import type { Content, ContentColumns, ContentTable } from 'pdfmake/interfaces';

import { IOffenseOptionItem } from '@/models';

import {
  IKasePrintFields,
  KasePrintSections,
  renderKasePrintInfoContent,
} from '../data';

type NarrativeTextPart = {
  text: string;
  isMention: boolean;
};

/**
 * Build an array of pdfmake inline text objects from a single Draft.js block,
 * preserving BOLD, ITALIC, UNDERLINE, STRIKETHROUGH, CODE inline styles and
 * MENTION / LINK entity styling.
 */
const buildInlineContent = (
  block: any,
  entityMap: Record<string, any>,
  baseStyle: string,
): any[] => {
  const text: string = block?.text ?? '';
  if (!text) return [{ text: '', style: baseStyle }];

  const inlineStyleRanges: any[] = block?.inlineStyleRanges ?? [];
  const entityRanges: any[] = block?.entityRanges ?? [];

  // Per-character active style sets
  const charStyles: Set<string>[] = Array.from(
    { length: text.length },
    () => new Set<string>(),
  );
  for (const range of inlineStyleRanges) {
    const start = Math.max(0, range.offset ?? 0);
    const end = Math.min(text.length, start + (range.length ?? 0));
    for (let i = start; i < end; i++) {
      charStyles[i].add(range.style);
    }
  }

  // Per-character entity key (stored as string to match entityMap keys)
  const charEntity: (string | null)[] = Array(text.length).fill(null);
  for (const range of entityRanges) {
    const start = Math.max(0, range.offset ?? 0);
    const end = Math.min(text.length, start + (range.length ?? 0));
    for (let i = start; i < end; i++) {
      charEntity[i] = String(range.key);
    }
  }

  // Fingerprint groups consecutive chars with identical style+entity
  const fingerprint = (i: number) =>
    `${[...charStyles[i]].sort().join(',')}|${charEntity[i] ?? ''}`;

  const segments: any[] = [];
  let segStart = 0;

  for (let i = 1; i <= text.length; i++) {
    if (i === text.length || fingerprint(i) !== fingerprint(segStart)) {
      const segText = text.slice(segStart, i);
      const styles = charStyles[segStart];
      const entityKey = charEntity[segStart];
      const entity = entityKey !== null ? entityMap[entityKey] ?? null : null;

      const seg: any = { text: segText, style: baseStyle };

      if (styles.has('BOLD')) seg.bold = true;
      if (styles.has('ITALIC')) seg.italics = true;

      const decorations: string[] = [];
      if (styles.has('UNDERLINE')) decorations.push('underline');
      if (styles.has('STRIKETHROUGH')) decorations.push('lineThrough');
      if (decorations.length === 1) seg.decoration = decorations[0];
      else if (decorations.length > 1) seg.decoration = decorations;

      if (styles.has('CODE')) {
        seg.font = 'Courier';
      }

      if (entity) {
        if (entity.type?.toUpperCase() === 'MENTION') {
          const stripped = segText.replace(/^@+/, '');
          seg.text =
            stripped ||
            String(entity.data?.value ?? entity.data?.text ?? segText);
          seg.style = 'mentionValue';
        } else if (entity.type?.toUpperCase() === 'LINK') {
          seg.color = '#0066cc';
          if (!seg.decoration) seg.decoration = 'underline';
        }
      }

      segments.push(seg);
      segStart = i;
    }
  }

  return segments.length ? segments : [{ text: ' ', style: baseStyle }];
};

/**
 * Convert a Draft.js RawContentState to an array of pdfmake Content items,
 * preserving block types (headings, lists, blockquote, code) and inline styles.
 *
 * @param rawContent  Draft.js RawContentState: { blocks: [...], entityMap: {...} }
 * @param options     Optional baseStyle override (defaults to 'fieldValue')
 */
export const draftjsRawToPdfmakeBlocks = (
  rawContent: any,
  options?: { baseStyle?: string },
): Content[] => {
  const baseStyle = options?.baseStyle ?? 'fieldValue';
  const blocks: any[] = rawContent?.blocks ?? [];
  const entityMap: Record<string, any> = rawContent?.entityMap ?? {};

  const result: Content[] = [];
  let pendingList: { type: 'ul' | 'ol'; items: Content[] } | null = null;

  const flushList = () => {
    if (pendingList) {
      result.push(
        pendingList.type === 'ul'
          ? ({ ul: pendingList.items, style: baseStyle } as any)
          : ({ ol: pendingList.items, style: baseStyle } as any),
      );
      pendingList = null;
    }
  };

  for (const block of blocks) {
    const isUl = block.type === 'unordered-list-item';
    const isOl = block.type === 'ordered-list-item';
    const isListItem = isUl || isOl;
    const listType: 'ul' | 'ol' = isUl ? 'ul' : 'ol';

    const inlineContent = buildInlineContent(block, entityMap, baseStyle);

    if (isListItem) {
      if (!pendingList || pendingList.type !== listType) {
        flushList();
        pendingList = { type: listType, items: [] };
      }
      pendingList.items.push({ text: inlineContent } as any);
      continue;
    }

    flushList();

    // Empty block → paragraph spacer
    if (!block.text) {
      result.push({
        text: ' ',
        style: baseStyle,
        margin: [0, 0, 0, 6] as [number, number, number, number],
      });
      continue;
    }

    const blockContent: any = {
      text: inlineContent,
      margin: [0, 0, 0, 6] as [number, number, number, number],
    };

    switch (block.type) {
      case 'header-one':
        blockContent.fontSize = 14;
        blockContent.bold = true;
        break;
      case 'header-two':
        blockContent.fontSize = 12;
        blockContent.bold = true;
        break;
      case 'header-three':
        blockContent.fontSize = 11;
        blockContent.bold = true;
        break;
      case 'header-four':
      case 'header-five':
      case 'header-six':
        blockContent.bold = true;
        break;
      case 'blockquote':
        blockContent.italics = true;
        blockContent.margin = [10, 0, 0, 6] as [number, number, number, number];
        break;
      case 'code-block':
        blockContent.font = 'Courier';
        break;
      default:
        blockContent.style = baseStyle;
    }

    result.push(blockContent);
  }

  flushList();
  return result;
};

const getNarrativeTextPartsForPrint = (
  data: Record<string, any>,
): NarrativeTextPart[] | null => {
  const raw = data?.content?.text;
  if (!raw || !Array.isArray(raw.blocks)) return null;

  const entityMap = raw.entityMap || {};
  const allParts: NarrativeTextPart[] = [];

  raw.blocks.forEach((block: any) => {
    const blockText = String(block?.text ?? '');
    if (!blockText) return;

    const ranges = Array.isArray(block?.entityRanges) ? block.entityRanges : [];
    if (!ranges.length) {
      allParts.push({ text: blockText, isMention: false });
      return;
    }

    const sortedRanges = [...ranges]
      .map((range: any) => ({
        offset: Number(range?.offset) || 0,
        length: Number(range?.length) || 0,
        key: range?.key,
      }))
      .filter((range) => range.length > 0)
      .sort((a, b) => a.offset - b.offset);

    let cursor = 0;
    sortedRanges.forEach((range) => {
      const start = Math.max(0, Math.min(range.offset, blockText.length));
      const end = Math.max(
        start,
        Math.min(start + range.length, blockText.length),
      );

      if (start > cursor) {
        allParts.push({
          text: blockText.slice(cursor, start),
          isMention: false,
        });
      }

      const entity = entityMap[String(range.key)] ?? entityMap[range.key];
      const segment = blockText.slice(start, end);
      const isMention = entity?.type?.toUpperCase?.() === 'MENTION';

      if (isMention) {
        const stripped = segment.replace(/^@+/, '');
        const mentionValue =
          stripped ||
          String(entity?.data?.value ?? entity?.data?.text ?? segment);

        allParts.push({
          text: mentionValue,
          isMention: true,
        });
      } else {
        allParts.push({ text: segment, isMention: false });
      }

      cursor = end;
    });

    if (cursor < blockText.length) {
      allParts.push({
        text: blockText.slice(cursor),
        isMention: false,
      });
    }
  });

  return allParts.length ? allParts : null;
};

/**
 * Render a field value for PDF
 * Handles both field-level and word-level redaction
 */
export const renderPDFField = (
  field: IKasePrintFields,
  data: Record<string, any>,
  sectionName: string,
  PropertyType?: Map<string, IOffenseOptionItem>,
  PropertyInvolvementList?: Map<string, IOffenseOptionItem>,
): Content => {
  // Get the field content first
  const content = renderKasePrintInfoContent(
    sectionName,
    field,
    data,
    PropertyType,
    PropertyInvolvementList,
  );
  const isNarrativeContentField =
    sectionName === KasePrintSections.NarrativesList &&
    field.value === 'content';
  const mentionTextParts = isNarrativeContentField
    ? getNarrativeTextPartsForPrint(data)
    : null;
  const plainContent =
    mentionTextParts?.map((part) => part.text).join('') || content || '';

  // Handle full field redaction
  if (field.redacted) {
    // Use the actual content length to maintain proper spacing
    const actualContent = plainContent || 'REDACTED';
    return {
      text: actualContent,
      background: '#000000',
      color: '#000000',
      style: 'redacted',
    };
  }

  // Handle word-level redaction
  if (field.redactedWords && field.redactedWords.size > 0) {
    const textContent = plainContent || ' ';

    // If content is empty or just ' ', return as is
    if (!textContent || textContent === ' ') {
      return {
        text: textContent,
        style: 'fieldValue',
      };
    }

    // Extract redacted words from the Set (they're stored as "word-start-end")
    const redactedWordsArray = Array.from(field.redactedWords).map((w) => {
      // If redactedWords are stored as "word-start-end", extract word
      const parts = w.split('-');
      if (
        parts.length >= 3 &&
        !isNaN(Number(parts[parts.length - 2])) &&
        !isNaN(Number(parts[parts.length - 1]))
      ) {
        return parts.slice(0, -2).join('-');
      }
      return w;
    });

    // Sort by length (longest first) to ensure longer words get matched first
    redactedWordsArray.sort((a, b) => b.length - a.length);

    // Build regex for all redacted words (exact word boundaries, unicode aware)
    const pattern = redactedWordsArray
      .map(
        (word) =>
          `(?<!\\p{L})${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?!\\p{L})`,
      )
      .join('|');

    if (!pattern) {
      return {
        text: textContent,
        style: 'fieldValue',
      };
    }

    const redactedRegex = new RegExp(pattern, 'giu');

    // Build a text array with mixed formatting (normal text + redacted blocks)
    const textParts: any[] = [];
    let lastIndex = 0;
    let match;

    // Reset regex to start from beginning
    redactedRegex.lastIndex = 0;

    while ((match = redactedRegex.exec(textContent)) !== null) {
      // Add normal text before the match
      if (match.index > lastIndex) {
        textParts.push({
          text: textContent.slice(lastIndex, match.index),
          style: 'fieldValue',
        });
      }

      // Add redacted block with black background and black text
      textParts.push({
        text: match[0], // Use actual text with black background to maintain positioning
        background: '#000000',
        color: '#000000',
        style: 'fieldValue',
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < textContent.length) {
      textParts.push({
        text: textContent.slice(lastIndex),
        style: 'fieldValue',
      });
    }

    // Return as a text array for inline formatting
    return {
      text: textParts,
    };
  }

  // No redaction — return rich formatted content for narrative fields
  if (isNarrativeContentField) {
    const raw = data?.content?.text;
    if (raw?.blocks) {
      const blocks = draftjsRawToPdfmakeBlocks(raw);
      return {
        stack: blocks.length ? blocks : [{ text: ' ', style: 'fieldValue' }],
      } as any;
    }
    // Fallback: mention-only flat rendering (raw blocks unavailable)
    if (mentionTextParts?.length) {
      const styledParts = mentionTextParts
        .filter((part) => part.text.length > 0)
        .map((part) => ({
          text: part.text,
          style: part.isMention ? 'mentionValue' : 'fieldValue',
        }));
      return {
        text: styledParts.length
          ? styledParts
          : [{ text: ' ', style: 'fieldValue' }],
      };
    }
  }

  return {
    text: content || ' ',
    style: 'fieldValue',
  };
};

/**
 * Create a 3-column grid of fields
 */
export const create3ColumnGrid = (
  fields: Array<{ label: string; value: Content }>,
): ContentColumns => {
  const columns: any[] = [];

  // Process fields in groups of 3
  for (let i = 0; i < fields.length; i += 3) {
    const chunk = fields.slice(i, i + 3);

    chunk.forEach((field) => {
      columns.push({
        stack: [{ text: field.label, style: 'fieldLabel' }, field.value],
        width: '*',
      });
    });

    // Fill remaining columns if less than 3
    while (columns.length % 3 !== 0 && columns.length < fields.length) {
      columns.push({ text: '', width: '*' });
    }
  }

  return {
    columns,
    columnGap: 10,
  };
};

/**
 * Create a table from data
 * Handles both simple values and Content objects (for redactions)
 */
export const createTable = (
  headers: string[],
  rows: any[][],
  widths?: (string | number)[],
): ContentTable => {
  return {
    table: {
      headerRows: 1,
      widths: widths || headers.map(() => '*'),
      body: [
        // Header row
        headers.map((header) => ({
          text: header,
          style: 'tableHeader',
        })),
        // Data rows
        ...rows.map((row) =>
          row.map((cell) => {
            // If cell is already a Content object with styling (e.g., for redactions), use it as-is
            if (
              typeof cell === 'object' &&
              cell !== null &&
              ('text' in cell || 'stack' in cell || 'columns' in cell)
            ) {
              // Preserve the Content object but ensure it has the tableCell style if no style is set
              return {
                ...cell,
                style: cell.style || 'tableCell',
              };
            }
            // Otherwise, wrap simple values
            return {
              text: cell || ' ',
              style: 'tableCell',
            };
          }),
        ),
      ],
    },
    layout: {
      // Outer borders removed — createCard wraps with an outer border
      hLineWidth: (i: number, node: any) =>
        i === 0 || i === node.table.body.length ? 0 : 1,
      vLineWidth: (i: number, node: any) =>
        i === 0 || i === node.table.widths.length ? 0 : 1,
      hLineColor: () => '#000',
      vLineColor: () => '#000',
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 2,
      paddingBottom: () => 2,
    },
    margin: [0, 0, 0, 0] as [number, number, number, number],
  };
};

/**
 * Create a horizontal divider line that stretches full width
 */
export const createDividerLine = (): Content => {
  return {
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: '',
            border: [false, false, false, true],
            borderColor: ['#000', '#000', '#000', '#000'],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: (i: number) => (i === 1 ? 1 : 0),
      hLineColor: () => '#000',
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [5, 3, 5, 3] as [number, number, number, number],
  };
};

/**
 * Create a card with title and content with separate borders
 * Title gets its own bordered box (centered), content gets separate bordered box
 */
export const createCard = (
  title: string,
  content: Content,
  addBorder = true,
): Content => {
  // Section title in its own bordered box, centered
  const titleBox = {
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: title,
            style: 'sectionTitle',
            alignment: 'center' as const,
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#000',
      vLineColor: () => '#000',
      paddingLeft: () => 5,
      paddingRight: () => 5,
      paddingTop: () => 1,
      paddingBottom: () => 1,
    },
    margin: [0, 0, 0, 3] as [number, number, number, number],
  };

  if (!addBorder) {
    return {
      stack: [titleBox, content],
      margin: [0, 0, 0, 10] as [number, number, number, number],
    };
  }

  // Always wrap content in a bordered box for consistent outer borders
  const contentBox = {
    table: {
      widths: ['*'],
      body: [[content]],
    },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#000',
      vLineColor: () => '#000',
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
    margin: [0, 0, 0, 0] as [number, number, number, number],
  };

  return {
    stack: [titleBox, contentBox],
    margin: [0, 0, 0, 10] as [number, number, number, number],
  };
};

/**
 * Create a card header (for multi-item cards)
 */
export const createCardHeader = (
  fields: Array<{ label: string; value: Content }>,
  columnsPerRow = 2,
): Content => {
  // If all fields fit in one row, use the original single-row layout
  if (fields.length <= columnsPerRow) {
    return {
      columns: fields.map(
        (field) =>
          ({
            text: [
              { text: `${field.label}: `, style: 'fieldLabel' },
              field.value,
            ],
            width: '*',
          } as any),
      ),
      style: 'cardHeader',
      margin: [5, 3, 5, 3] as [number, number, number, number],
    };
  }

  // Multi-row layout
  const rows: Content[] = [];

  for (let i = 0; i < fields.length; i += columnsPerRow) {
    const chunk = fields.slice(i, i + columnsPerRow);
    const columns: any[] = chunk.map((field) => ({
      text: [{ text: `${field.label}: `, style: 'fieldLabel' }, field.value],
      width: '*',
    }));

    // Fill remaining columns in the last row
    while (columns.length < columnsPerRow) {
      columns.push({ text: '', width: '*' });
    }

    rows.push({
      columns,
      columnGap: 10,
      margin: [5, 3, 5, 3] as [number, number, number, number],
      style: 'cardHeader',
    });
  }

  return { stack: rows };
};

/**
 * Create detail fields grid
 */
export const createDetailGrid = (
  fields: Array<{ label: string; value: Content }>,
  columnsPerRow = 3,
): Content => {
  if (columnsPerRow === 1) {
    // Full width layout for narratives
    return {
      stack: fields.map((field) => ({
        stack: [field.value],
        margin: [5, 3, 5, 3] as [number, number, number, number],
      })),
    };
  }

  // Multi-column layout
  const rows: Content[] = [];

  for (let i = 0; i < fields.length; i += columnsPerRow) {
    const chunk = fields.slice(i, i + columnsPerRow);
    const columns: any[] = chunk.map((field) => ({
      text: [{ text: `${field.label}: `, style: 'fieldLabel' }, field.value],
      width: '*',
    }));

    // Fill remaining columns
    while (columns.length < columnsPerRow) {
      columns.push({ text: '', width: '*' });
    }

    rows.push({
      columns,
      columnGap: 10,
      margin: [5, 3, 5, 3] as [number, number, number, number],
    });
  }

  return { stack: rows };
};

/**
 * Format a date/time string
 */
export const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return ' ';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Safe text renderer (handles null/undefined)
 */
export const safeText = (value: any): string => {
  if (value === null || value === undefined || value === '') {
    return ' ';
  }
  return String(value);
};
